import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import "./warehousePicker.css";

// Components
import SearchBar from "../Other Components/searchBar";
import ItemCard from "../Item Components/itemCard";
import Button from "../Other Components/button";
import Title from "../Other Components/Title";
import PageLoader from "../Other Components/pageLoader";

// Functions and hooks
import { GetWarehouses } from "../../BackendAccess/WarehouseHooks";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";
import { useTranslation } from "react-i18next";

export default function WarehousePicker({
  title = "Warehouses",
  onChange = () => {},
  disabled,
  singleSelect = false,
  propertiesToShow = {
    ID: { Show: true, Width: 1 },
    Name: { Show: true, Width: 3 },
  },
  className,
  initWarehouses,
  selectable = true,
}) {
  const finalClassName = twMerge("warehouse-picker w-100", className);
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(false);

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();

  useEffect(() => {
    GetAllWarehouses();
  }, []);

  useEffect(() => {
    if (
      selectedWarehouses.length === 0 &&
      initWarehouses &&
      initWarehouses.length > 0
    )
      setSelectedWarehouses(initWarehouses);
    onChange(initWarehouses);
  }, []);

  const columnWidths = Object.values(propertiesToShow).map(
    (item) => item.Width
  );

  const GetAllWarehouses = async () => {
    try {
      const [data, getWarehousesError] = await GetWarehouses();
      if (getWarehousesError) {
        setError(getWarehousesError);
        return;
      }
      setWarehouses(data);
    } catch (err) {
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (term) => {
    if (!disabled) setSearchTerm(term);
  };

  const handleSearchFocus = () => {
    if (!disabled) setSearching(true);
  };

  const handleSearchBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget) && !disabled) {
      setSearching(false);
    }
  };

  const handleClickOutside = (e) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target) &&
      !disabled
    ) {
      setSearching(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const filteredWarehouses = warehouses.filter((item) => {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return (
      item.Name.toLowerCase().includes(lowerCaseTerm) ||
      item.WarehouseID.toString().toLowerCase().includes(lowerCaseTerm)
    );
  });

  const sortedWarehouses = [...filteredWarehouses].sort((a, b) =>
    a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1
  );

  const handleCreateClick = async () => {
    if (!disabled) navigate("/workspace/warehouses/warehouseCreation");
  };
  const handleSecondAction = async (id) => {
    if (!disabled)
      navigate("/workspace/warehouses/warehouseEditPage", { state: { id } });
  };

  const createCardProps = (item) => {
    const cardPropArray = [
      { Name: "ID", Value: item.WarehouseID, columnWidth: columnWidths[0] },
      { Name: "Name", Value: item.Name, columnWidth: columnWidths[1] },
    ];

    const newCardPropArr = cardPropArray.filter((item) => {
      return propertiesToShow[item.Name].Show;
    });
    return newCardPropArr;
  };

  const setIDSelected = (id, state) => {
    var newSelectedIds = [...selectedWarehouses];
    if (state) {
      if (!singleSelect || newSelectedIds.length === 0) {
        newSelectedIds.push(id);
      } else if (newSelectedIds.length !== 0 && singleSelect) {
        newSelectedIds = [];
        newSelectedIds.push(id);
      }
    } else {
      newSelectedIds = newSelectedIds.filter((item) => item !== id);
    }

    setSelectedWarehouses(newSelectedIds);
    onChange(newSelectedIds);
  };

  return (
    <div className={finalClassName} ref={containerRef} tabIndex={-1}>
      {disabled ? <div className="disabledDimmingCover"></div> : null}
      <Title className="h-auto w-100 font-l mb-1">{title}</Title>
      <div className="warehouse-picker-content">
        {selectable ? (
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            shadow={false}
            placeholder={t("Search warehouses...", { ns: "warehouse" })}
          />
        ) : null}

        <div className="warehouse-picker-lists-container">
          <PageLoader loading={loading} error={pageError}>
            <ul
              className={
                searching || !selectable
                  ? "list-warehouse-picker"
                  : "list-warehouse-picker collapsed"
              }
            >
              {sortedWarehouses.map((item) => (
                <li
                  key={item.WarehouseID}
                  className="warehouse-picker-list-item"
                  onClick={handleSearchFocus}
                >
                  <ItemCard
                    key={item.WarehouseID}
                    ItemID={item.WarehouseID}
                    propertiesToShow={createCardProps(item)}
                    bBtnVisible={false}
                    bBodyClickable={selectable}
                    bBtnSectionVisible={false}
                    bSelectable={true}
                    onSelected={() => {}}
                    selectedItemIds={selectedWarehouses}
                    setIDSelected={selectable ? setIDSelected : () => {}}
                    onSecondAction={() => handleSecondAction(item.WarehouseID)}
                  />
                </li>
              ))}
            </ul>

            <ul className="list-picked-warehouses">
              {!loading && selectedWarehouses.length > 0 ? (
                selectedWarehouses.map((warehouseId) => {
                  const warehouse = warehouses.find(
                    (wh) => wh.WarehouseID === warehouseId
                  );
                  return warehouse ? (
                    <li
                      key={warehouseId}
                      className="picked-warehouses-list-item"
                    >
                      {" "}
                      <ItemCard
                        key={warehouse.WarehouseID}
                        ItemID={warehouse.WarehouseID}
                        propertiesToShow={createCardProps(warehouse)}
                        bBtnVisible={false}
                        bBodyClickable={true}
                        bBtnSectionVisible={false}
                        bSelectable={true}
                        onSelected={() => {}}
                        selectedItemIds={selectedWarehouses}
                        setIDSelected={selectable ? setIDSelected : () => {}}
                      />
                    </li>
                  ) : null;
                })
              ) : (
                <p className="cardPlaceholder Warehouse">
                  {t("No selected warehouses", { ns: "warehouse" })}
                </p>
              )}
            </ul>
            {selectable ? (
              <Button
                btnSize={"btn-normal"}
                btnStyle={"btn-positive"}
                className=" w-90 mb-2"
                btnMargin={"btn-default"}
                onClick={handleCreateClick}
                icon={"create"}
              >
                {t("Create Warehouse", { ns: "warehouse" })}
              </Button>
            ) : null}
          </PageLoader>
        </div>
      </div>
    </div>
  );
}
